import * as React from 'react';
import { describeInstrument, LedgerItem, partiesOf } from './ledgerInstrument';

/**
 * How wide the Party A and Party B columns need to be for the tree they show:
 * as wide as the longest cell in each, so a column of short names doesn't
 * leave a gap before the date. `_columns.scss` caps them at their usual width,
 * past which a name is cut short as before.
 */

/** The cell's padding, either side of its name (`.tree-ledger-cell`). */
const CELL_PADDING = 16;
/** Between Party B's name and the "+N" after it (`.tree-ledger-party`). */
const MORE_GAP = 6;
/** The "+N" tag's own padding and border (`.tree-ledger-party__more`). */
const MORE_BOX = 10;
/** No narrower than the date's column, so the headings above them fit. */
const MIN_WIDTH = 88;

export interface PartyCellText {
  /** Party A's name. */
  a: string;
  /** Party B's name, and the "+N" after it when there are more parties. */
  b: string;
  more: string | null;
}

/** The party cells of every instrument row in a tree, expanded or not. */
export const partyCellsIn = (items: LedgerItem[]): PartyCellText[] => {
  const cells: PartyCellText[] = [];
  const walk = (nodes: LedgerItem[]) =>
    nodes.forEach((node) => {
      if (describeInstrument(node).isInstrument) {
        const [a = '', b = '', ...others] = partiesOf(node);
        cells.push({ a, b, more: others.length ? `+${others.length}` : null });
      }
      walk((node.children as LedgerItem[] | undefined) ?? []);
    });
  walk(items);
  return cells;
};

export interface PartyColumnWidths {
  a: number;
  b: number;
}

/** The widths the cells need, given a way to measure a name and a "+N". */
export const partyColumnWidths = (
  cells: PartyCellText[],
  measureName: (text: string) => number,
  measureMore: (text: string) => number
): PartyColumnWidths => {
  let a = 0;
  let b = 0;
  cells.forEach((cell) => {
    if (cell.a) a = Math.max(a, measureName(cell.a));
    const more = cell.more ? MORE_GAP + MORE_BOX + measureMore(cell.more) : 0;
    if (cell.b || more) b = Math.max(b, measureName(cell.b) + more);
  });
  const width = (text: number) =>
    Math.max(MIN_WIDTH, Math.ceil(text) + CELL_PADDING);
  return { a: width(a), b: width(b) };
};

/** Text measured in a font, through a canvas; null where there is none (tests). */
const textMeasure = (font: string): ((text: string) => number) | null => {
  const context = document.createElement('canvas').getContext?.('2d');
  if (!context) return null;
  context.font = font;
  return (text) => context.measureText(text).width;
};

/**
 * The party columns' widths for a tree, as the custom properties the ledger's
 * grid reads; set them on the explorer column. Empty until they can be
 * measured, which leaves the columns at their usual width.
 */
export const usePartyColumnWidths = (
  items: LedgerItem[] | undefined
): React.CSSProperties =>
  React.useMemo(() => {
    if (!items?.length) return {};
    const family =
      getComputedStyle(document.documentElement)
        .getPropertyValue('--joy-fontFamily-body')
        .trim() || 'Roboto, sans-serif';
    const measureName = textMeasure(`12px ${family}`);
    const measureMore = textMeasure(`500 11px ${family}`);
    if (!measureName || !measureMore) return {};

    const { a, b } = partyColumnWidths(
      partyCellsIn(items),
      measureName,
      measureMore
    );
    return {
      '--tree-ledger-party-a-width': `${a}px`,
      '--tree-ledger-party-b-width': `${b}px`,
    } as React.CSSProperties;
  }, [items]);
